import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { Compass, Star } from 'lucide-react';

export const Rule13Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule13 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule13', { ...data, [field]: value });
  };
  
  const values = [
    'Family', 'Security', 'Freedom', 'Health', 'Faith/Spirituality',
    'Education', 'Adventure', 'Creativity', 'Helping Others', 'Independence',
    'Community', 'Career Growth', 'Relationships', 'Peace of Mind', 'Legacy'
  ];
  
  const [selectedValues, setSelectedValues] = useState(data.topValues || []);
  
  const toggleValue = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : selectedValues.length < 5 ? [...selectedValues, value] : selectedValues;
    setSelectedValues(newValues);
    updateData('topValues', newValues);
  };
  
  return (
    <WorkbookPage pageId="rule-13" prevPage="rule-12" nextPage="rule-14">
      <RuleHeader ruleNumber="13" title="Find Your Purpose" section="healing" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Money without purpose is just numbers. When you connect your financial goals to your 
        deepest values and life purpose, managing money becomes meaningful—not just another 
        chore. This rule helps you discover what truly matters to you and align your money 
        with those values.
      </Paragraph>
      
      <SectionTitle>Why Purpose Matters</SectionTitle>
      <Paragraph>
        When your "why" is clear, the "how" becomes easier. Saving money is hard when you're 
        just "supposed to." But saving for your child's education, your family's security, 
        or the freedom to pursue your dreams? That's motivating.
      </Paragraph>
      
      <Callout type="tip">
        <strong>Money is a tool for building the life you want.</strong> The first step is 
        knowing what kind of life that is.
      </Callout>
      
      <SectionTitle>Identify Your Core Values</SectionTitle>
      <Paragraph>
        Select your top 5 values from the list below. These are the things that matter most 
        to you—what you'd want your life to be about.
      </Paragraph>
      
      <div className="my-6">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {values.map((value) => (
            <button
              key={value}
              onClick={() => toggleValue(value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedValues.includes(value)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
        {selectedValues.length > 0 && (
          <div className="mt-4 p-4 bg-primary/5 rounded-lg">
            <p className="text-sm font-medium mb-2">Your selected values:</p>
            <div className="flex flex-wrap gap-2">
              {selectedValues.map((value) => (
                <span key={value} className="flex items-center gap-1 text-sm">
                  <Star className="h-3 w-3 text-primary fill-primary" />
                  {value}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <ExampleBox title="Values-Based Money Decisions">
        <p><strong>Value: Family</strong></p>
        <p className="text-sm text-muted-foreground mb-3">
          → I'll prioritize saving for family experiences over buying things.
          → I'll build an emergency fund so my family is protected.
        </p>
        <p><strong>Value: Freedom</strong></p>
        <p className="text-sm text-muted-foreground mb-3">
          → I'll pay off debt so I'm not tied down by payments.
          → I'll build savings so I have choices in my career.
        </p>
        <p><strong>Value: Helping Others</strong></p>
        <p className="text-sm text-muted-foreground">
          → I'll include giving in my budget.
          → I'll get my finances stable so I can help more.
        </p>
      </ExampleBox>
      
      <StepList steps={[
        "Review the values list and select your top 5.",
        "Reflect: Why are these most important to you?",
        "Consider: How do your current money habits align with these values?",
        "Identify conflicts: Are you spending on things that don't match your values?",
        "Write a purpose statement connecting your values to your financial goals.",
        "Use this purpose statement when money decisions feel hard."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">
          <Compass className="h-5 w-5 inline mr-2" />
          Your Purpose Worksheet
        </h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="Why do these values matter to you? What experiences shaped them?"
            placeholder="Reflect on why you chose these specific values..."
            value={data.valuesExplanation || ''}
            onChange={(v) => updateData('valuesExplanation', v)}
            multiline
          />
          
          <WorksheetInput
            label="How well do your current spending habits align with these values? (1-10)"
            placeholder="Be honest with yourself..."
            value={data.alignmentScore || ''}
            onChange={(v) => updateData('alignmentScore', v)}
          />
          
          <WorksheetInput
            label="What money habits conflict with your values?"
            placeholder="Where is there a mismatch?"
            value={data.conflicts || ''}
            onChange={(v) => updateData('conflicts', v)}
            multiline
          />
          
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-5">
            <label className="block font-semibold mb-3">
              ✨ Your Financial Purpose Statement
            </label>
            <p className="text-sm text-muted-foreground mb-3">
              Complete this sentence: "I manage my money well because..."
            </p>
            <textarea
              className="worksheet-textarea"
              placeholder="I manage my money well because... [connect to your values and what matters most]"
              value={data.purposeStatement || ''}
              onChange={(e) => updateData('purposeStatement', e.target.value)}
              rows={4}
            />
          </div>
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I've identified my top 5 values
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I've reflected on how my money habits align (or don't)
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I've written a financial purpose statement
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Money without purpose is just numbers",
        "Your values guide meaningful financial decisions",
        "A purpose statement keeps you motivated",
        "Align spending with what truly matters",
        "When your 'why' is clear, 'how' becomes easier"
      ]} />
      
      <AffirmationBox>
        My money serves my deepest values and life purpose. I am building the life I truly want.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="What came up for you during this exercise?"
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
